import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useNotifications } from '@/hooks/useNotifications';

interface SubscriptionData {
  subscribed: boolean;
  subscription_tier: string | null;
  subscription_end: string | null;
  is_trial: boolean;
  trial_end: string | null;
  trial_days_left: number;
}

interface SubscriptionContextType {
  subscriptionData: SubscriptionData | null;
  isLoading: boolean;
  checkSubscription: () => Promise<void>;
  createCheckout: () => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  hasAccess: boolean;
  isTrialActive: boolean;
  isSubscribedUser: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// Cache pour éviter les appels répétés
const CACHE_KEY = 'subscription_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedData {
  data: SubscriptionData;
  timestamp: number;
}

const getCachedData = (): SubscriptionData | null => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const { data, timestamp }: CachedData = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        // Using cached subscription data
        return data;
      }
    }
  } catch (error) {
    console.warn('[SUBSCRIPTION] Cache read error:', error);
  }
  return null;
};

const setCachedData = (data: SubscriptionData) => {
  try {
    const cacheData: CachedData = {
      data,
      timestamp: Date.now()
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('[SUBSCRIPTION] Cache write error:', error);
  }
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { success, error } = useNotifications();
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState<number>(0);

  const checkSubscription = async (forceRefresh = false) => {
    if (!user) {
      // No user available, skipping subscription check
      setSubscriptionData(null);
      sessionStorage.removeItem(CACHE_KEY);
      return;
    }

    // Éviter les appels trop fréquents (minimum 2 minutes entre les appels)
    const now = Date.now();
    if (!forceRefresh && now - lastCheckTime < 2 * 60 * 1000) {
      // Rate limiting subscription checks
      return;
    }

    // Vérifier le cache d'abord
    if (!forceRefresh) {
      const cached = getCachedData();
      if (cached) {
        setSubscriptionData(cached);
        return;
      }
    }

    try {
      setIsLoading(true);
      // Checking subscription status
      
      const { data, error: functionError } = await supabase.functions.invoke('check-subscription');
      
      if (functionError) {
        console.error('[SUBSCRIPTION] Error checking subscription:', functionError);
        throw functionError;
      }

      // Subscription data received successfully
      setSubscriptionData(data);
      setCachedData(data);
      setLastCheckTime(now);
    } catch (err) {
      console.error('[SUBSCRIPTION] Failed to check subscription:', err);
      // En cas d'erreur, utiliser les données en cache si disponibles
      const cached = getCachedData();
      if (cached) {
        // Using cached data after error
        setSubscriptionData(cached);
      } else {
        error({
          title: 'Erreur',
          description: 'Impossible de vérifier le statut d\'abonnement'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const createCheckout = async () => {
    if (!user) {
      error({
        title: 'Erreur',
        description: 'Vous devez être connecté pour vous abonner'
      });
      return;
    }

    try {
      setIsLoading(true);
      // Creating checkout session
      
      const { data, error: functionError } = await supabase.functions.invoke('create-checkout');
      
      // Checkout response received
      
      if (functionError) {
        console.error('[SUBSCRIPTION] Error creating checkout:', functionError);
        throw functionError;
      }

      if (data?.url) {
        // Redirecting to checkout - always use location.href for better compatibility
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('[SUBSCRIPTION] Failed to create checkout:', err);
      
      // Message d'erreur plus détaillé
      const errorMessage = err?.message || 'Erreur inconnue';
      error({
        title: 'Erreur de paiement',
        description: `Impossible de créer la session de paiement: ${errorMessage}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openCustomerPortal = async () => {
    if (!user) {
      error({
        title: 'Erreur',
        description: 'Vous devez être connecté pour gérer votre abonnement'
      });
      return;
    }

    try {
      setIsLoading(true);
      // Opening customer portal
      
      const { data, error: functionError } = await supabase.functions.invoke('customer-portal');
      
      if (functionError) {
        console.error('[SUBSCRIPTION] Error opening portal:', functionError);
        
        // Détection si c'est un problème de configuration Stripe
        const errorMsg = functionError?.message || '';
        if (errorMsg.includes('configuration') || errorMsg.includes('default configuration')) {
          error({
            title: 'Configuration requise',
            description: 'Le portail client Stripe doit être configuré. Consultez la documentation Stripe pour activer le Customer Portal.'
          });
        } else {
          throw functionError;
        }
        return;
      }

      if (data?.url) {
        // Redirecting to customer portal - use location.href for better compatibility
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL received');
      }
    } catch (err) {
      console.error('[SUBSCRIPTION] Failed to open customer portal:', err);
      const errorMessage = err?.message || 'Erreur inconnue';
      error({
        title: 'Erreur portail client',
        description: `Impossible d'ouvrir le portail client: ${errorMessage}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Vérifier l'abonnement seulement au changement d'utilisateur (SUPPRESSION DU SETINTERVAL)
  useEffect(() => {
    if (user) {
      checkSubscription();
    } else {
      setSubscriptionData(null);
      sessionStorage.removeItem(CACHE_KEY);
    }
  }, [user]);

  // Computed properties - Mode restreint par défaut
  const isTrialActive = subscriptionData?.is_trial && (subscriptionData?.trial_days_left ?? 0) > 0;
  const isSubscribedUser = subscriptionData?.subscribed ?? false;
  // Par défaut, les nouveaux utilisateurs sont en mode restreint
  // Accès complet seulement si abonnement actif ou essai valide
  // IMPORTANT: Si subscriptionData est null (pas encore chargé), l'utilisateur est en mode démo
  const hasAccess = subscriptionData !== null && (isSubscribedUser || isTrialActive);

  const value: SubscriptionContextType = {
    subscriptionData,
    isLoading,
    checkSubscription: () => checkSubscription(false),
    createCheckout,
    openCustomerPortal,
    hasAccess,
    isTrialActive,
    isSubscribedUser
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};